/**
 * MCP Protocol Tools for HoloScript Publishing Protocol
 *
 * 4 tools for AI agent protocol operations:
 * - holo_protocol_publish: Publish composition to protocol
 * - holo_protocol_collect: Collect a published composition
 * - holo_protocol_revenue: Preview revenue distribution
 * - holo_protocol_lookup: Look up published composition
 */

import { Tool } from '@modelcontextprotocol/sdk/types.js';

// =============================================================================
// Tool Definitions
// =============================================================================

export const protocolTools: Tool[] = [
  {
    name: 'holo_protocol_publish',
    description:
      'Publish a HoloScript composition to the HoloScript Protocol. ' +
      'Registers the composition on-chain (content hash, author, imports, license) ' +
      'and deploys to CDN. Returns protocol ID, collect URL, and revenue preview.',
    inputSchema: {
      type: 'object',
      properties: {
        code: {
          type: 'string',
          description: 'The HoloScript source code to publish',
        },
        author: {
          type: 'string',
          description: 'Author name or wallet address',
        },
        license: {
          type: 'string',
          enum: ['free', 'cc_by', 'cc_by_sa', 'cc_by_nc', 'commercial', 'exclusive'],
          description: 'License type for the composition (default: free)',
        },
        price: {
          type: 'string',
          description: 'Collect price in ETH (default: "0" for free collect)',
        },
        mintAsNFT: {
          type: 'boolean',
          description: 'Also mint as Zora NFT on Base L2 (default: false)',
        },
      },
      required: ['code', 'author'],
    },
  },
  {
    name: 'holo_protocol_collect',
    description:
      'Collect (mint an edition of) a published HoloScript composition. ' +
      'Revenue is automatically distributed to creator, platform, and upstream import authors.',
    inputSchema: {
      type: 'object',
      properties: {
        contentHash: {
          type: 'string',
          description: 'SHA-256 content hash of the composition to collect',
        },
        referrer: {
          type: 'string',
          description: 'Referrer address (earns 2% referral reward)',
        },
        quantity: {
          type: 'number',
          description: 'Number of editions to collect (default: 1)',
        },
        collectorId: {
          type: 'string',
          description: 'Collector identity paying for the edition',
        },
      },
      required: ['contentHash'],
    },
  },
  {
    name: 'holo_protocol_revenue',
    description:
      'Preview the revenue distribution for a composition. ' +
      'Shows how collect revenue would flow to creator, platform (2.5%), ' +
      'import authors (5% per level, max 3 levels), and referrer (2%).',
    inputSchema: {
      type: 'object',
      properties: {
        price: {
          type: 'string',
          description: 'Collect price in ETH to simulate',
        },
        author: {
          type: 'string',
          description: 'Creator/author identifier',
        },
        imports: {
          type: 'array',
          description: 'Import chain nodes for upstream royalty calculation',
          items: {
            type: 'object',
            properties: {
              contentHash: { type: 'string' },
              author: { type: 'string' },
              depth: { type: 'number' },
            },
          },
        },
        referrer: {
          type: 'string',
          description: 'Referrer address (optional)',
        },
      },
      required: ['price', 'author'],
    },
  },
  {
    name: 'holo_protocol_lookup',
    description:
      'Look up a published composition by content hash or author. ' +
      'Returns the protocol record with on-chain metadata, collect URL, and edition count.',
    inputSchema: {
      type: 'object',
      properties: {
        contentHash: {
          type: 'string',
          description: 'SHA-256 content hash to look up',
        },
        author: {
          type: 'string',
          description: 'Author address to list all publications',
        },
      },
    },
  },
];

// =============================================================================
// Tool Handler
// =============================================================================

export async function handleProtocolTool(
  name: string,
  args: Record<string, unknown>
): Promise<unknown | null> {
  switch (name) {
    case 'holo_protocol_publish':
      return handlePublish(args);
    case 'holo_protocol_collect':
      return handleCollect(args);
    case 'holo_protocol_revenue':
      return handleRevenue(args);
    case 'holo_protocol_lookup':
      return handleLookup(args);
    default:
      return null;
  }
}

// =============================================================================
// Input validation helpers
// =============================================================================

const CONTENT_HASH_RE = /^[a-f0-9]{64}$/;
const AUTHOR_RE = /^[\w.-]+$/;
const VALID_LICENSES = new Set([
  'free',
  'cc_by',
  'cc_by_sa',
  'cc_by_nc',
  'commercial',
  'exclusive',
]);

function validateContentHash(value: unknown): string {
  if (typeof value !== 'string' || !CONTENT_HASH_RE.test(value)) {
    throw new Error(
      `Invalid contentHash ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â must be a 64-character lowercase hex string`
    );
  }
  return value;
}

function validateAuthor(value: unknown): string {
  if (typeof value !== 'string' || !AUTHOR_RE.test(value)) {
    throw new Error(
      `Invalid author ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â must contain only word characters, dots, and hyphens`
    );
  }
  return value;
}

/**
 * Runtime type guard for required string parameters.
 * Returns the string if valid, or a structured error response if not.
 * Prevents TypeErrors from `as string` casts when MCP callers send null/number/object.
 */
function requireString(
  value: unknown,
  fieldName: string
): string | { status: 'error'; error: string; message: string } {
  if (typeof value !== 'string') {
    return {
      status: 'error',
      error: 'INVALID_PARAMS',
      message: `${fieldName} must be a string, got ${value === null ? 'null' : value === undefined ? 'undefined' : typeof value}`,
    };
  }
  return value;
}

/**
 * Runtime type guard for optional string parameters.
 * Returns the string if present, undefined if absent/null/non-string.
 */
function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function optionalStringOrError(
  value: unknown,
  fieldName: string
): string | undefined | { status: 'error'; error: string; message: string } {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'string') {
    return {
      status: 'error',
      error: 'INVALID_PARAMS',
      message: `${fieldName} must be a string, got ${typeof value}`,
    };
  }
  return value;
}

/**
 * Runtime type guard for number parameters with optional clamping.
 * Returns the number if valid (or default), or a structured error response if non-numeric.
 */
function requireNumberInRange(
  value: unknown,
  fieldName: string,
  defaultValue: number,
  min: number,
  max: number
): number | { status: 'error'; error: string; message: string } {
  if (value === undefined || value === null) return defaultValue;
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return {
      status: 'error',
      error: 'INVALID_PARAMS',
      message: `${fieldName} must be a finite number, got ${typeof value}`,
    };
  }
  return Math.max(min, Math.min(max, value));
}

interface ImportError {
  index: number;
  field: string;
  message: string;
}

/**
 * Runtime type guard for the imports array parameter.
 * Validates that each element has the expected shape, returning a structured
 * error response if any element is malformed.
 */
function validateImportsArray(value: unknown):
  | {
      imports: Array<{ contentHash: string; author: string; depth: number }>;
      errors: ImportError[];
    }
  | { status: 'error'; error: string; message: string } {
  if (value === undefined || value === null) return { imports: [], errors: [] };
  if (!Array.isArray(value)) {
    return {
      status: 'error',
      error: 'INVALID_PARAMS',
      message: `imports must be an array, got ${typeof value}`,
    };
  }
  const imports: Array<{ contentHash: string; author: string; depth: number }> = [];
  const errors: ImportError[] = [];
  for (let i = 0; i < value.length; i++) {
    const item = value[i];
    if (typeof item !== 'object' || item === null) {
      errors.push({
        index: i,
        field: '',
        message: `imports[${i}] must be an object, got ${item === null ? 'null' : typeof item}`,
      });
      continue;
    }
    const obj = item as Record<string, unknown>;
    // Validate contentHash with regex guard (ATK-A2: path traversal via imports array)
    if (typeof obj.contentHash !== 'string' || !CONTENT_HASH_RE.test(obj.contentHash)) {
      errors.push({
        index: i,
        field: 'contentHash',
        message:
          typeof obj.contentHash !== 'string'
            ? `imports[${i}].contentHash must be a string`
            : `imports[${i}].contentHash must be a 64-character lowercase hex string`,
      });
    }
    // Validate author with regex guard (ATK-C1: type confusion via imports array)
    if (typeof obj.author !== 'string' || !AUTHOR_RE.test(obj.author)) {
      errors.push({
        index: i,
        field: 'author',
        message:
          typeof obj.author !== 'string'
            ? `imports[${i}].author must be a string`
            : `imports[${i}].author must contain only word characters, dots, and hyphens`,
      });
    }
    if (obj.depth !== undefined && typeof obj.depth !== 'number') {
      errors.push({ index: i, field: 'depth', message: `imports[${i}].depth must be a number` });
    }
    // Skip only this item if it has errors (previous `errors.length > 0` skipped ALL items after first error)
    const itemHasErrors = errors.some((e) => e.index === i);
    if (itemHasErrors) continue;
    imports.push({
      contentHash: obj.contentHash as string,
      author: obj.author as string,
      depth: typeof obj.depth === 'number' ? obj.depth : 1,
    });
  }
  if (errors.length > 0) {
    return {
      status: 'error',
      error: 'INVALID_PARAMS',
      message: errors.map((e) => e.message).join('; '),
    };
  }
  return { imports, errors: [] };
}

interface ProtocolRecord {
  contentHash?: unknown;
  author?: unknown;
  price?: unknown;
  importHashes?: unknown;
}

interface ProtocolRevenueRecordResult {
  creatorId: string;
  grossAmount: number;
  eventId: string;
}

function bigintToSafeRevenueAmount(amount: bigint): number {
  if (amount <= 0n) return 0;
  const max = BigInt(Number.MAX_SAFE_INTEGER);
  return amount > max ? Number.MAX_SAFE_INTEGER : Number(amount);
}

async function fetchProtocolRecord(
  serverUrl: string,
  contentHash: string
): Promise<ProtocolRecord | null> {
  const res = await fetch(`${serverUrl}/api/protocol/${contentHash}`);
  if (!res.ok) return null;
  const body = (await res.json()) as unknown;
  return body && typeof body === 'object' ? (body as ProtocolRecord) : null;
}

async function recordProtocolCollectRevenue({
  record,
  contentHash,
  quantity,
  collectorId,
  referrer,
  ledgerEntryId,
}: {
  record: ProtocolRecord | null;
  contentHash: string;
  quantity: number;
  collectorId: string;
  referrer?: string;
  ledgerEntryId?: string;
}): Promise<ProtocolRevenueRecordResult | null> {
  if (!record || typeof record.author !== 'string') return null;

  const price = typeof record.price === 'string' ? record.price : '0';
  const { calculateRevenueDistribution, ethToWei } = await import('@holoscript/core');
  const totalPriceWei = ethToWei(String(price));
  const totalPrice = BigInt(totalPriceWei.toString()) * BigInt(quantity);
  const dist = calculateRevenueDistribution(totalPrice, record.author, [], { referrer });
  const creatorFlow = dist.flows.find(
    (flow: { recipient: string; reason: string; amount: bigint }) =>
      flow.reason === 'creator' && flow.recipient === record.author
  );
  if (!creatorFlow) return null;

  const grossAmount = bigintToSafeRevenueAmount(creatorFlow.amount);
  if (grossAmount <= 0) return null;

  const { recordProtocolCreatorRevenue } = await import('./economy-tools');
  const event = recordProtocolCreatorRevenue({
    creatorId: record.author,
    contentHash,
    grossAmount,
    collectorId,
    ledgerEntryId,
  });

  return {
    creatorId: event.creatorId,
    grossAmount: event.grossAmount,
    eventId: event.id,
  };
}

// =============================================================================
// Individual Handlers
// =============================================================================

async function handlePublish(args: Record<string, unknown>) {
  const codeResult = requireString(args.code, 'code');
  if (typeof codeResult !== 'string') return codeResult;
  const code = codeResult;

  const authorResult = requireString(args.author, 'author');
  if (typeof authorResult !== 'string') return authorResult;
  const author = authorResult;

  const rawLicense = optionalString(args.license);
  const license = rawLicense && VALID_LICENSES.has(rawLicense) ? rawLicense : 'free';

  const rawPrice = optionalString(args.price);
  const price = rawPrice ?? '0';

  const mintAsNFT = args.mintAsNFT === true;

  // Dynamic imports to avoid circular deps
  const {
    generateProvenance,
    calculateRevenueDistribution,
    formatRevenueDistribution,
    ethToWei,
    PROTOCOL_CONSTANTS,
    parse,
  } = await import('@holoscript/core');

  // Parse and generate provenance
  const ast = parse(code);
  if (ast.errors && ast.errors.length > 0) {
    return {
      status: 'error',
      error: 'PARSE_ERROR',
      message: `Parse errors: ${ast.errors.map((e: { message?: string }) => e.message || String(e)).join(', ')}`,
    };
  }

  const provenance = generateProvenance(code, ast, { author, license: license as 'free' });

  // Calculate revenue preview
  const priceWei = ethToWei(price);
  const importChain = provenance.imports.map(
    (imp: { hash?: string; path: string; author?: string }, i: number) => ({
      contentHash: imp.hash || `import-${i}`,
      author: imp.author || imp.path,
      depth: 1,
      children: [],
    })
  );
  const revenuePreview = calculateRevenueDistribution(priceWei, author, importChain);

  // Try to register via server
  const serverUrl = process.env.HOLOSCRIPT_SERVER_URL || 'https://mcp.holoscript.net';
  try {
    // Internal MCP tool dispatch authenticates via the server-side
    // HOLOSCRIPT_API_KEY (legacy-key auth path on http-server.ts). External
    // callers must register a client at POST /oauth/register; this is the
    // in-process privileged loopback.
    const apiKey = process.env.HOLOSCRIPT_API_KEY || '';
    const authHeaders: Record<string, string> = {
      'Content-Type': 'application/json; charset=utf-8',
    };
    if (apiKey) authHeaders['x-mcp-api-key'] = apiKey;

    // Store metadata
    await fetch(`${serverUrl}/api/protocol/metadata`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        contentHash: provenance.hash,
        provenance: {
          hash: provenance.hash,
          author: provenance.author,
          license: provenance.license,
          publishMode: provenance.publishMode,
          imports: provenance.imports,
          created: provenance.created,
        },
      }),
    });

    // Register protocol record
    const res = await fetch(`${serverUrl}/api/protocol`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        contentHash: provenance.hash,
        author,
        importHashes: provenance.imports.map((imp: { hash?: string }) => imp.hash).filter(Boolean),
        license,
        publishMode: provenance.publishMode,
        price,
        referralBps: PROTOCOL_CONSTANTS.DEFAULT_REFERRAL_BPS,
        metadataURI: `${serverUrl}/metadata/${provenance.hash}`,
        mintAsNFT,
        code,
        title: `MCP Published: ${author}`,
        description: 'Published via MCP protocol tool',
      }),
    });

    if (!res.ok) {
      const text = await res.text();
      return { status: 'error', error: 'PUBLISH_FAILED', message: text };
    }

    const result = await res.json();
    return {
      status: 'success',
      ...result,
      revenuePreview: formatRevenueDistribution(revenuePreview),
      provenance: {
        hash: provenance.hash,
        publishMode: provenance.publishMode,
        license: provenance.license,
        importCount: provenance.imports.length,
      },
    };
  } catch (err) {
    return {
      status: 'error',
      error: 'SERVER_UNAVAILABLE',
      message: err instanceof Error ? err.message : String(err),
      provenance: {
        hash: provenance.hash,
        publishMode: provenance.publishMode,
        license: provenance.license,
      },
    };
  }
}

async function handleCollect(args: Record<string, unknown>) {
  let contentHash: string;
  try {
    contentHash = validateContentHash(args.contentHash);
  } catch (err) {
    return { status: 'error', error: 'INVALID_PARAMS', message: (err as Error).message };
  }
  const referrer = optionalString(args.referrer);
  const quantityResult = requireNumberInRange(args.quantity, 'quantity', 1, 1, 10000);
  if (typeof quantityResult !== 'number') return quantityResult;
  const quantity = quantityResult;
  const collectorResult = optionalStringOrError(args.collectorId ?? args.collector, 'collectorId');
  if (collectorResult && typeof collectorResult !== 'string') return collectorResult;
  const collectorId = collectorResult ?? 'anonymous-collector';

  const serverUrl = process.env.HOLOSCRIPT_SERVER_URL || 'https://mcp.holoscript.net';

  // Internal MCP tool dispatch authenticates via legacy-key (same pattern
  // as handlePublish ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â external callers register via POST /oauth/register).
  const apiKey = process.env.HOLOSCRIPT_API_KEY || '';
  const authHeaders: Record<string, string> = {
    'Content-Type': 'application/json; charset=utf-8',
  };
  if (apiKey) authHeaders['x-mcp-api-key'] = apiKey;

  try {
    const record = await fetchProtocolRecord(serverUrl, contentHash);
    const res = await fetch(`${serverUrl}/api/collect/${contentHash}`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({ referrer, quantity, collectorId }),
    });

    if (!res.ok) {
      const text = await res.text();
      return { status: 'error', error: 'COLLECT_FAILED', message: text };
    }

    const collectResult = (await res.json()) as Record<string, unknown>;
    const revenueRecorded = await recordProtocolCollectRevenue({
      record,
      contentHash,
      quantity,
      collectorId,
      referrer,
      ledgerEntryId: typeof collectResult.txHash === 'string' ? collectResult.txHash : undefined,
    });

    return { status: 'success', ...collectResult, collectorId, revenueRecorded };
  } catch (err) {
    return {
      status: 'error',
      error: 'SERVER_UNAVAILABLE',
      message: err instanceof Error ? err.message : String(err),
    };
  }
}

async function handleRevenue(args: Record<string, unknown>) {
  const priceResult = requireString(args.price, 'price');
  if (typeof priceResult !== 'string') return priceResult;
  const authorResult = requireString(args.author, 'author');
  if (typeof authorResult !== 'string') return authorResult;

  const { calculateRevenueDistribution, formatRevenueDistribution, ethToWei } =
    await import('@holoscript/core');

  const price = priceResult;
  const author = authorResult;
  const referrer = optionalString(args.referrer);
  const importsResult = validateImportsArray(args.imports);
  if (!('imports' in importsResult)) return importsResult;
  const imports = importsResult.imports;

  const priceWei = ethToWei(price);
  const importChain = imports.map((imp) => ({
    contentHash: imp.contentHash,
    author: imp.author,
    depth: imp.depth || 1,
    children: [] as Array<{
      contentHash: string;
      author: string;
      depth: number;
      children: unknown[];
    }>,
  }));

  const dist = calculateRevenueDistribution(priceWei, author, importChain, { referrer });

  return {
    status: 'success',
    totalPrice: price,
    flows: dist.flows.map((f: any) => ({
      recipient: f.recipient,
      amount: f.amount.toString(),
      reason: f.reason,
      depth: f.depth,
      percentage: `${(f.bps / 100).toFixed(1)}%`,
    })),
    formatted: formatRevenueDistribution(dist),
  };
}

async function handleLookup(args: Record<string, unknown>) {
  if (!args.contentHash && !args.author) {
    return {
      status: 'error',
      error: 'MISSING_PARAMS',
      message: 'Provide either contentHash or author',
    };
  }

  let contentHash: string | undefined;
  let author: string | undefined;
  try {
    if (args.contentHash !== undefined) contentHash = validateContentHash(args.contentHash);
    if (args.author !== undefined) author = validateAuthor(args.author);
  } catch (err) {
    return { status: 'error', error: 'INVALID_PARAMS', message: (err as Error).message };
  }

  const serverUrl = process.env.HOLOSCRIPT_SERVER_URL || 'https://mcp.holoscript.net';

  try {
    const endpoint = contentHash
      ? `${serverUrl}/api/protocol/${contentHash}`
      : `${serverUrl}/api/protocol/author/${author}`;

    const res = await fetch(endpoint);
    if (!res.ok) {
      if (res.status === 404) {
        return {
          status: 'not_found',
          message: contentHash
            ? `No record for hash ${contentHash}`
            : `No publications by ${author}`,
        };
      }
      const text = await res.text();
      return { status: 'error', error: 'LOOKUP_FAILED', message: text };
    }

    return { status: 'success', ...(await res.json()) };
  } catch (err) {
    return {
      status: 'error',
      error: 'SERVER_UNAVAILABLE',
      message: err instanceof Error ? err.message : String(err),
    };
  }
}
